/**
 * Root layout — the responsive web shell.
 *
 * Sidebar nav on desktop (≥860px); bottom tab bar on mobile. The sidebar
 * shows the merchant's avatar + display name + settle-to address, with
 * nav entries for Home / Activity / Settings, plus Webhooks / API keys
 * when Developer mode is on.
 *
 * The /setup route bypasses this shell — onboarding renders its own
 * centered card layout via the OnboardingShell wrapper inside the setup
 * route component.
 */

import { useEffect, useState } from "react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { ApiError, getMyMerchant, type MerchantProfile } from "../lib/api";
import { isDeveloperMode } from "../lib/developer-mode";
import { signOut } from "../lib/session";
import { Ic } from "../components/Icons";
import { Btn } from "../components/ui";
import { useI18n, LOCALES, type Locale } from "../lib/i18n";

const TAB_DESKTOP: Array<{
  to:
    | "/"
    | "/quickstart"
    | "/activity"
    | "/pages"
    | "/unlocks"
    | "/products"
    | "/links"
    | "/coins"
    | "/billing"
    | "/status"
    | "/settings"
    | "/webhooks"
    | "/api-keys"
    | "/ads"
    | "/stats";
  labelKey: string;
  icon: "home" | "activity" | "settings" | "code" | "key" | "shield" | "share" | "zap" | "wallet" | "sparkles" | "lock" | "tag" | "link";
  developerOnly?: boolean;
  adminOnly?: boolean;
}> = [
  { to: "/", labelKey: "nav.home", icon: "home" },
  { to: "/quickstart", labelKey: "nav.quickstart", icon: "sparkles" },
  { to: "/activity", labelKey: "nav.activity", icon: "activity" },
  { to: "/coins", labelKey: "nav.coins", icon: "wallet" },
  { to: "/products", labelKey: "nav.products", icon: "tag" },
  { to: "/links", labelKey: "nav.links", icon: "link" },
  { to: "/pages", labelKey: "nav.pages", icon: "share" },
  { to: "/unlocks", labelKey: "nav.unlock", icon: "lock" },
  { to: "/billing", labelKey: "nav.plan", icon: "shield" },
  { to: "/status", labelKey: "nav.status", icon: "zap" },
  { to: "/settings", labelKey: "nav.settings", icon: "settings" },
  { to: "/stats", labelKey: "nav.stats", icon: "zap", adminOnly: true },
  { to: "/ads", labelKey: "nav.ads", icon: "share", adminOnly: true },
  { to: "/webhooks", labelKey: "nav.webhooks", icon: "code", developerOnly: true },
  { to: "/api-keys", labelKey: "nav.apikeys", icon: "key", developerOnly: true },
];

export function RootLayout() {
  const location = useLocation();
  const router = useRouter();
  const navigate = useNavigate();
  const { t: tr, locale, setLocale } = useI18n();
  const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [booting, setBooting] = useState(true);
  const developerMode = isDeveloperMode();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      void navigate({ to: "/login" });
    }
  };

  const isOnboarding = location.pathname === "/setup";
  // /claim self-manages auth (it may run pre-login) and renders its own
  // full-screen card — treat it like the auth pages: no boot fetch, no chrome.
  const isClaimPage = location.pathname === "/claim";
  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/signup" ||
    isClaimPage;

  useEffect(() => {
    // Skip the boot fetch on auth pages — the user isn't authed yet, and
    // hitting /api/admin/merchants/me without a session returns 401, which
    // would render the boot error needlessly.
    if (isAuthPage) {
      setBooting(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const m = await getMyMerchant();
        if (cancelled) return;
        setMerchant(m);
        if (!m.exists && !isOnboarding) {
          void navigate({ to: "/setup" });
        }
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof ApiError && e.status === 401
            ? "We couldn't authenticate this Telegram session. Open the dashboard from inside Telegram (not a normal browser)."
            : e instanceof Error
              ? e.message
              : String(e);
        setBootError(msg);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOnboarding, isAuthPage, navigate]);

  // /login + /signup render full-screen (no dashboard chrome)
  if (isAuthPage) {
    return <Outlet />;
  }

  // Onboarding gets its own centered shell (no sidebar)
  if (isOnboarding) {
    return (
      <div className="app-web onboard-web">
        <div className="onboard-top">
          <Wordmark size={19} />
        </div>
        <div className="onboard-wrap">
          <div className="onboard-card">
            <div className="scroll">
              <div className="pad">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (booting) {
    return (
      <div className="app-web">
        <div className="web-main">
          <div className="pad" style={{ padding: 40 }}>
            <div className="muted" style={{ color: "var(--ink-3)" }}>
              {tr("shell.loading")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="app-web">
        <div className="web-main">
          <div className="pad" style={{ maxWidth: 480, padding: 40 }}>
            <div className="banner fail">
              <Ic name="warn" size={16} />
              <div>{bootError}</div>
            </div>
            <Btn
              variant="secondary"
              onClick={() => void router.invalidate()}
              style={{ marginTop: 16 }}
            >
              {tr("shell.try_again")}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  const merchantAvatarLetter = (merchant?.display_name ?? "S")
    .charAt(0)
    .toUpperCase();
  const settleSummary = merchant?.default_settlement
    ? `${merchant.default_settlement.chain} · USDC`
    : tr("shell.not_set");

  const isPlatformAdmin = merchant?.is_platform_admin === true;
  const visibleTabs = TAB_DESKTOP.filter(
    (t) => (!t.developerOnly || developerMode) && (!t.adminOnly || isPlatformAdmin),
  );

  return (
    <div className="app-web">
      <aside className="sidebar">
        <div className="side-brand">
          <Wordmark size={21} />
        </div>
        <div className="side-merchant">
          <div className="av">{merchantAvatarLetter}</div>
          <div style={{ minWidth: 0 }}>
            <div className="mn">{merchant?.display_name ?? tr("shell.merchant")}</div>
            <div className="ms">{settleSummary}</div>
          </div>
        </div>
        <nav className="side-nav" aria-label="Primary">
          <div className="side-cap">{tr("shell.workspace")}</div>
          {visibleTabs.map((t) => {
            const active =
              location.pathname === t.to ||
              (t.to === "/activity" &&
                location.pathname.startsWith("/activity"));
            return (
              <Link
                key={t.to}
                to={t.to}
                className={`side-item${active ? " active" : ""}`}
              >
                <Ic name={t.icon} size={18} />
                {tr(t.labelKey)}
              </Link>
            );
          })}
        </nav>
        <div className="side-foot">
          <a
            className="side-item"
            href="https://discord.gg/8AmvrMmvMp"
            target="_blank"
            rel="noopener"
            style={{ marginBottom: 4 }}
          >
            <Ic name="share" size={18} />
            Discord
          </a>
          <LangSwitcher locale={locale} setLocale={setLocale} label={tr("shell.language")} />
          <button
            type="button"
            className="side-item"
            onClick={() => void handleSignOut()}
            disabled={signingOut}
            style={{
              width: "100%",
              background: "none",
              border: "none",
              cursor: "pointer",
              font: "inherit",
              marginBottom: 10,
            }}
          >
            <Ic name="back" size={18} />
            {signingOut ? tr("shell.signing_out") : tr("shell.sign_out")}
          </button>
          Shieldz Payments · {tr("shell.tagline")}
        </div>
      </aside>

      <main className="web-main">
        <div className="web-top">
          <h1>{tr(titleKeyForPath(location.pathname))}</h1>
          <div className="row">
            {location.pathname !== "/" && (
              <Link to="/" className="web-back">
                <Ic name="back" size={16} />
                {tr("nav.home")}
              </Link>
            )}
            <button
              type="button"
              className="web-back"
              onClick={() => void handleSignOut()}
              disabled={signingOut}
              style={{ background: "none", border: "none", cursor: "pointer", font: "inherit" }}
            >
              <Ic name="back" size={16} />
              {signingOut ? tr("shell.signing_out") : tr("shell.sign_out")}
            </button>
          </div>
        </div>
        <div className="web-body">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tabs (visible <860px via CSS) */}
      <nav className="web-tabbar" aria-label="Primary">
        {visibleTabs.slice(0, 4).map((t) => {
          const active =
            location.pathname === t.to ||
            (t.to === "/activity" && location.pathname.startsWith("/activity"));
          return (
            <Link
              key={t.to}
              to={t.to}
              className={`tab${active ? " active" : ""}`}
            >
              <Ic name={t.icon} size={20} />
              {tr(t.labelKey)}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

function Wordmark({ size = 21 }: { size?: number }) {
  const markSize = Math.round(size * 1.4);
  return (
    <span
      className="wm"
      style={{
        fontSize: size,
        fontWeight: 600,
        letterSpacing: "-0.045em",
        display: "inline-flex",
        alignItems: "center",
        gap: Math.round(size * 0.42),
      }}
    >
      <img
        src="/brand/logo-sz.png"
        alt=""
        aria-hidden="true"
        style={{
          width: markSize,
          height: markSize,
          objectFit: "contain",
          flex: "none",
          display: "block",
        }}
      />
      <span>
        shield<span className="z" style={{ color: "var(--brand-500)" }}>z</span>
      </span>
    </span>
  );
}

function titleKeyForPath(pathname: string): string {
  if (pathname === "/") return "nav.home";
  if (pathname === "/quickstart") return "nav.quickstart";
  if (pathname.startsWith("/activity/")) return "title.payment_link";
  if (pathname === "/activity") return "nav.activity";
  if (pathname === "/pages") return "title.public_pages";
  if (pathname === "/coins") return "nav.coins";
  if (pathname === "/billing") return "nav.plan";
  if (pathname === "/status") return "nav.status";
  if (pathname === "/stats") return "title.platform_stats";
  if (pathname === "/settings") return "nav.settings";
  if (pathname === "/webhooks") return "nav.webhooks";
  if (pathname === "/api-keys") return "nav.apikeys";
  return "Shieldz";
}

function LangSwitcher({
  locale,
  setLocale,
  label,
}: {
  locale: Locale;
  setLocale: (l: Locale) => void;
  label: string;
}) {
  return (
    <label
      className="side-item"
      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer" }}
    >
      <Ic name="globe" size={18} />
      <span className="sr-only" style={{ position: "absolute", left: -9999 }}>
        {label}
      </span>
      <select
        aria-label={label}
        value={locale}
        onChange={(e) => setLocale(e.target.value as Locale)}
        style={{
          flex: 1,
          background: "none",
          border: "none",
          color: "inherit",
          font: "inherit",
          cursor: "pointer",
          outline: "none",
        }}
      >
        {LOCALES.map((l) => (
          <option key={l.code} value={l.code} style={{ color: "#111" }}>
            {l.label}
          </option>
        ))}
      </select>
    </label>
  );
}
